import React, { useEffect, useState } from "react"
import {
  Card,
  Col,
  Container,
  Row,
  CardBody,
  CardTitle,
  Label,
  Form,
  Input,
  FormFeedback,
  Button,
  CardHeader,
  Table,
} from "reactstrap"

import { useFormik } from "formik"
import * as Yup from "yup"
import toast from "react-hot-toast"
import {
  useCreateRoleMutation,
  useLazyGetRolePermissionsQuery,
  useUpdateRoleMutation,
} from "store/api/roles/roles.api"
import { Link, useNavigate } from "react-router-dom"
import { moduleLabels } from "constants/permissions"
import Spinners from "components/Common/Spinner"
import eye from "../../assets/images/icons/eye.svg"
import edit from "../../assets/images/icons/edit.svg"
import deleteIcon from "../../assets/images/icons/delete-red.svg"
import add from "../../assets/images/icons/add.svg"
import list from "../../assets/images/icons/list.svg"
import LoaderComponent from "components/loader"

const RolesForm = ({ defaultValues, id = null, HeaderComponent = null }) => {
  const [createRole, { isLoading }] = useCreateRoleMutation()
  const [updateRole, { isLoading: isUpdating }] = useUpdateRoleMutation()
  const [getPermissions, { isFetching }] = useLazyGetRolePermissionsQuery()
  const [allPermissions, setAllPermissions] = useState([])
  const history = useNavigate()

  document.title = "Create Role | Prachay"

  useEffect(() => {
    const onGetPermissions = async () => {
      const { data } = await getPermissions().unwrap()
      setAllPermissions(data)
    }
    onGetPermissions()
  }, [])

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: { ...defaultValues },
    validationSchema: Yup.object({}),
    onSubmit: async (values) => {
      if (id) await onUpdateRole(values)
      else await onCreateRole(values)
    },
  })

  const onCreateRole = async (values) => {
    try {
      const { data, message, success } = await createRole(values).unwrap()
      if (success) {
        history("/user-management#ROLE")
        toast.success(message, { id: "submitSuccess" })
      }
    } catch (error) {
      const data = error?.data
      if (data?.errors) {
        const { errors } = data
        validation.setErrors(errors)
      }

      toast.error(error?.data?.message, { id: "toastMessage" })
    }
  }

  const onUpdateRole = async (values) => {
    try {
      const { data, message, success } = await updateRole({
        id,
        ...values,
      }).unwrap()
      if (success) {
        history("/user-management#ROLE")
        toast.success(message, { id: "submitSuccess" })
      }
    } catch (error) {
      const data = error?.data
      if (data?.errors) {
        const { errors } = data
        validation.setErrors(errors)
      }

      toast.error(error?.data?.message, { id: "toastMessage" })
    }
  }

  return (
    <React.Fragment>
      <Row>
        <Col xl={12}>
          <Card>
            <CardBody>
              {HeaderComponent && <HeaderComponent />}
              <Form
                className="form-horizontal"
                onSubmit={(e) => {
                  e.preventDefault()
                  validation.handleSubmit()
                  return false
                }}
              >
                <Row className="mb-2">
                  <Col xl={6}>
                    <Label className="col-form-label">
                      Name <span className="error">*</span>
                    </Label>
                    <Input
                      name="name"
                      className="form-control"
                      placeholder="Enter Name"
                      type="text"
                      onChange={validation.handleChange}
                      onBlur={validation.handleBlur}
                      value={validation.values.name || ""}
                      invalid={
                        validation.touched.name && validation.errors.name
                          ? true
                          : false
                      }
                    />
                    {validation.touched.name && validation.errors.name ? (
                      <FormFeedback type="invalid">
                        {validation.errors.name}
                      </FormFeedback>
                    ) : null}
                  </Col>
                  <Col xl={6}>
                    <Label className="col-form-label">
                      Description <span>(Optional)</span>
                    </Label>
                    <Input
                      name="description"
                      className="form-control"
                      placeholder="Enter description"
                      type="text"
                      onChange={validation.handleChange}
                      onBlur={validation.handleBlur}
                      value={validation.values.description || ""}
                      invalid={
                        validation.touched.description &&
                        validation.errors.description
                          ? true
                          : false
                      }
                    />
                    {validation.touched.description &&
                    validation.errors.description ? (
                      <FormFeedback type="invalid">
                        {validation.errors.description}
                      </FormFeedback>
                    ) : null}
                  </Col>
                  <Col xl={12}>
                    <hr />
                    <h5>Permissions</h5>
                    <hr />
                    <div
                      className={`${
                        validation.touched.permissions &&
                        validation.errors.permissions &&
                        "is-invalid"
                      }`}
                    >
                      {isFetching ? (
                        <LoaderComponent />
                      ) : (
                        <div className="table-bordered-wrapper user-role-table">
                          <div className={"table-responsive"}>
                            <Table hover>
                              <thead>
                                <tr>
                                  <th></th>
                                  <th>
                                    <div className="d-flex align-items-center justify-content-center gap-2">
                                      All
                                    </div>
                                  </th>
                                  <th>
                                    <div className="d-flex align-items-center justify-content-center gap-2">
                                      <img src={list} alt="" /> List
                                    </div>
                                  </th>
                                  <th>
                                    <div className="d-flex align-items-center justify-content-center gap-2">
                                      <img src={eye} alt="" /> View
                                    </div>
                                  </th>
                                  <th>
                                    <div className="d-flex align-items-center justify-content-center gap-2">
                                      <img src={add} alt="" /> Add
                                    </div>
                                  </th>
                                  <th>
                                    <div className="d-flex align-items-center justify-content-center gap-2">
                                      <img src={edit} alt="" /> Edit
                                    </div>
                                  </th>
                                  <th>
                                    <div className="d-flex align-items-center justify-content-center gap-2">
                                      <img src={deleteIcon} alt="" /> Delete
                                    </div>
                                  </th>
                                </tr>
                              </thead>

                              <tbody>
                                {allPermissions.map((permission, index) => (
                                  <>
                                    {permission.module_label !==
                                      "PERMISSION" && (
                                      <tr key={`allPermissions_${index}`}>
                                        <td>
                                          {
                                            moduleLabels[
                                              permission.module_label
                                            ]
                                          }
                                        </td>
                                        <td>
                                          <div className="user-table-td">
                                            <input
                                              type="checkbox"
                                              name="permissions"
                                              className="form-check-input"
                                              onChange={(e) => {
                                                const { checked } = e.target
                                                const itemKeys =
                                                  permission.items.map(
                                                    (item) => item.key
                                                  )
                                                const newPermissions = checked
                                                  ? [
                                                      ...new Set([
                                                        ...validation.values
                                                          .permissions,
                                                        ...itemKeys,
                                                      ]),
                                                    ]
                                                  : validation.values.permissions.filter(
                                                      (perm) =>
                                                        !itemKeys.includes(perm)
                                                    )

                                                validation.setFieldValue(
                                                  "permissions",
                                                  newPermissions
                                                )
                                              }}
                                              checked={permission.items.every(
                                                (item) =>
                                                  validation.values.permissions.includes(
                                                    item.key
                                                  )
                                              )}
                                            />
                                          </div>
                                        </td>
                                        {permission.items.map((item, j) => (
                                          <td
                                            key={`allPermissions_${index}_${j}`}
                                          >
                                            <div className="user-table-td">
                                              <input
                                                type="checkbox"
                                                name="permissions"
                                                id={item.key}
                                                value={item.key}
                                                className="form-check-input"
                                                onChange={(e) => {
                                                  const { checked, value } =
                                                    e.target
                                                  const newPermissions = checked
                                                    ? [
                                                        ...validation.values
                                                          .permissions,
                                                        value,
                                                      ]
                                                    : validation.values.permissions.filter(
                                                        (v) => v !== value
                                                      )
                                                  validation.setFieldValue(
                                                    "permissions",
                                                    newPermissions
                                                  )
                                                }}
                                                onBlur={validation.handleBlur}
                                                checked={validation.values.permissions.includes(
                                                  item.key
                                                )}
                                              />
                                            </div>
                                          </td>
                                        ))}
                                      </tr>
                                    )}
                                  </>
                                ))}
                              </tbody>
                            </Table>
                          </div>
                        </div>
                      )}
                    </div>

                    {validation.touched.permissions &&
                    validation.errors.permissions ? (
                      <FormFeedback type="invalid">
                        {validation.errors.permissions}
                      </FormFeedback>
                    ) : null}
                  </Col>
                </Row>
                <Row className="justify-content-end mt-4">
                  <Col sm={12}>
                    <div className="d-flex justify-content-end align-items-center gap-3">
                      <Link
                        to={"/user-management#ROLE"}
                        type="button"
                        className="btn btn-cancel w-md"
                      >
                        Cancel
                      </Link>
                      <Button
                        type="submit"
                        className="btn btn-primary w-md"
                        disabled={isLoading || isUpdating}
                      >
                        {isLoading || isUpdating ? (
                          <Spinners />
                        ) : !id ? (
                          `Create Role`
                        ) : (
                          "Update Role"
                        )}
                        {}
                      </Button>
                    </div>
                  </Col>
                </Row>
              </Form>
            </CardBody>
          </Card>
        </Col>
      </Row>
    </React.Fragment>
  )
}

export default RolesForm
